// The words of the TECHNOHULL Vessel Entry & Work Control Form (ΕΝΤΥΠΟ ΕΙΣΟΔΟΥ ΣΚΑΦΟΥΣ & ΕΛΕΓΧΟΥ ΕΡΓΑΣΙΩΝ),
// exactly as the customer's Word files have them (section 2 from the revised arrival inspection): Greek, then
// English, in the files' order and accents. The dialog (components/VesselEntryForm.tsx) shows each label's
// two halves (stacked on phones) and the printout (vesselEntryPrint.ts) joins them with " / ", as the paper
// does. The form is bilingual by design, so there is no language choice for it.

import type { CheckResult, FunctionalKey, PhotosAnswer, VisualKey } from '@technohull/shared';

/** a bilingual label: its Greek half, its English half, and (one label) a Greek tail after the English. */
export interface Bi { el: string; en: string; tail?: string }

/** the label as the paper prints it: "Greek / English". */
export const biText = (b: Bi): string => `${b.el} / ${b.en}${b.tail ? ` / ${b.tail}` : ''}`;

export const VEF_TITLE: Bi = { el: 'ΕΝΤΥΠΟ ΕΙΣΟΔΟΥ ΣΚΑΦΟΥΣ & ΕΛΕΓΧΟΥ ΕΡΓΑΣΙΩΝ', en: 'VESSEL ENTRY & WORK CONTROL FORM' };

/** the five section bars, numbered as on the form. */
export const VEF_SECTIONS = {
  vessel: { el: '1. ΣΤΟΙΧΕΙΑ ΣΚΑΦΟΥΣ & ΕΡΓΟΥ', en: 'VESSEL & PROJECT PARTICULARS' },
  arrival: { el: '2. ΑΦΙΞΗ & ΑΡΧΙΚΟΣ ΕΛΕΓΧΟΣ ΚΑΤΑΣΤΑΣΗΣ', en: 'ARRIVAL & INITIAL CONDITION CHECK' },
  admin: { el: '3. ΔΙΟΙΚΗΤΙΚΗ ΚΑΤΑΧΩΡΗΣΗ & ΕΛΕΓΧΟΣ ΣΥΣΤΗΜΑΤΩΝ', en: 'ADMINISTRATION & SYSTEM CONTROL' },
  scope: { el: '4. ΕΛΕΓΧΟΣ ΑΝΤΙΚΕΙΜΕΝΟΥ ΕΡΓΑΣΙΩΝ', en: 'SCOPE OF WORK CONTROL' },
  schedule: { el: '5. ΠΡΟΓΡΑΜΜΑ, ΕΚΤΕΛΕΣΗ, ΟΛΟΚΛΗΡΩΣΗ & ΠΑΡΑΔΟΣΗ', en: 'SCHEDULE, EXECUTION, COMPLETION & HANDOVER' },
} satisfies Record<string, Bi>;

/** section 2's three parts: the arrival check's two tables, then who inspected it. */
export const VEF_PARTS = {
  visual: { el: 'A. ΟΠΤΙΚΟΣ ΕΛΕΓΧΟΣ ΚΑΤΑ ΤΗΝ ΑΦΙΞΗ', en: 'ARRIVAL VISUAL CONDITION' },
  functional: { el: 'B. ΒΑΣΙΚΟΣ ΛΕΙΤΟΥΡΓΙΚΟΣ ΕΛΕΓΧΟΣ', en: 'BASIC FUNCTIONAL CHECK' },
  record: { el: 'C. ΕΠΙΒΕΒΑΙΩΣΗ ΚΑΤΑΓΡΑΦΗΣ', en: 'INSPECTION RECORD ACKNOWLEDGMENT' },
} satisfies Record<string, Bi>;

/** every field's label (the colon the paper puts after it is added where it prints). */
export const VEF_LABELS = {
  vesselName: { el: 'Όνομα σκάφους', en: 'Vessel Name' },
  typeModelSap: { el: 'Τύπος / Μοντέλο', en: 'Vessel Type / Model / SAP No.', tail: 'Αρ. SAP' },
  arrival: { el: 'Ημερομηνία άφιξης', en: 'Arrival date' },
  item: { el: 'Σημείο ελέγχου', en: 'Inspection item' },
  result: { el: 'Αποτέλεσμα', en: 'Result' },
  comment: { el: 'Σχόλιο', en: 'Ref.' },
  inspectedBy: { el: 'Τεχνικός Επιθεώρησης', en: 'Inspected by' },
  inspectedOn: { el: 'Ημερομηνία', en: 'Date' },
  technician: { el: 'Τεχνικός Εργασιών', en: 'Technician' },
  signature: { el: 'Υπογραφή', en: 'Signature' },
  photos: { el: 'Φωτογραφίες', en: 'Photos' },
  rolodexEntry: { el: 'Καταχώρηση στο Rolodex ολοκληρώθηκε', en: 'Rolodex entry completed' },
  jobFileOpened: { el: 'Φάκελος έργου δημιουργήθηκε', en: 'Project job file opened' },
  registeredOn: { el: 'Ημερομηνία καταχώρησης', en: 'Date Registered' },
  registeredBy: { el: 'Υπεύθυνος καταχώρησης', en: 'Registered by' },
  workRequestReceived: { el: 'Αρχικό αίτημα / λίστα εργασιών πελάτη ελήφθη', en: 'Initial client work request / work list received' },
  scopeIssued: { el: 'Τελικό / αναθεωρημένο Scope of Work εκδόθηκε', en: 'Final / revised Scope of Work issued' },
  criticalItems: { el: 'Κρίσιμες εκκρεμότητες / ειδικές οδηγίες', en: 'Critical pending items / special instructions' },
  worksCommenced: { el: 'Έναρξη εργασιών', en: 'Works Commenced' },
  estimatedCompletion: { el: 'Εκτιμώμενη ολοκλήρωση', en: 'Estimated Completion' },
  handover: { el: 'Παράδοση', en: 'Handover Date' },
} satisfies Record<string, Bi>;

/** the disclaimer under the Arrival date: what the arrival check is, and what it is not. */
export const VEF_DISCLAIMER: Bi = {
  el: 'Σύντομη οπτική και ασφαλής λειτουργική καταγραφή ΠΡΙΝ την έναρξη εργασιών.',
  en: 'Rapid pre-work visual and safe functional baseline; not a full diagnostic survey or sea trial.',
};

/** the note under the visual check: the engines are looked at from outside only. */
export const VEF_ENGINES_NOTE = {
  label: { el: 'ΚΙΝΗΤΗΡΕΣ', en: 'ENGINES' } satisfies Bi,
  text: {
    el: 'Μόνο βασικός εξωτερικός οπτικός έλεγχος. Χωρίς εκκίνηση, μετρήσεις, διαγνωστικά ή έλεγχο χειριστηρίων πρόωσης.',
    en: 'External visual inspection ONLY. No engine start-up, measurements, diagnostics or propulsion-control tests.',
  } satisfies Bi,
};

/** A. the arrival visual condition's seven rows, in the form's order. */
export const VEF_VISUAL: readonly { key: VisualKey; label: Bi }[] = [
  { key: 'hull', label: { el: 'Γάστρα, gelcoat, πλώρη και πλευρές – εμφανή χτυπήματα/γρατζουνιές', en: 'Hull, gelcoat, bow and topsides – visible marks/damage' } },
  { key: 'tubes', label: { el: 'Αεροθάλαμοι, βαλβίδες και περιμετρικά προστατευτικά', en: 'Inflatable tubes, valves and rubbing strakes' } },
  { key: 'deck', label: { el: 'Κατάστρωμα, teak, πλατφόρμα, σκάλα και δέστρες', en: 'Deck, teak, platform, boarding ladder and cleats' } },
  { key: 'windshield', label: { el: 'Παρμπρίζ, hardtop, βάσεις και στεγανοποιήσεις', en: 'Windshield, hardtop, supports and visible seals' } },
  { key: 'seating', label: { el: 'Καθίσματα, ταπετσαρίες, κονσόλα και εξωτερικά έπιπλα', en: 'Seats, upholstery, helm console and deck furniture' } },
  { key: 'engines', label: { el: 'Μηχανές και προπέλες – μόνο βασικός εξωτερικός οπτικός έλεγχος', en: 'Engines and propellers – basic external visual condition ONLY, where visible' } },
  { key: 'keys', label: { el: 'Παραδοθέντα κλειδιά, καλύμματα, εξαρτήματα / κινητός εξοπλισμός', en: 'Keys, covers, accessories and loose equipment received' } },
];

/** B. the basic functional check's six rows, in the form's order. */
export const VEF_FUNCTIONAL: readonly { key: FunctionalKey; label: Bi }[] = [
  { key: 'chartplotter', label: { el: 'Chartplotter / MFD – εκκίνηση, οθόνη και βασικές ενδείξεις', en: 'Chartplotter / MFD – power-up and basic display' } },
  { key: 'gps', label: { el: 'GPS – λήψη στίγματος και ένδειξη θέσης', en: 'GPS – fix acquisition and position indication' } },
  { key: 'navLights', label: { el: 'Φώτα ναυσιπλοΐας και αγκυροβολίας', en: 'Navigation and anchor lights' } },
  { key: 'deckLights', label: { el: 'Φώτα καταστρώματος, cockpit, καμπίνας / λοιπά', en: 'Deck, cockpit, cabin and other installed lights' } },
  { key: 'bilgePumps', label: { el: 'Αντλίες σεντινών – όπου εφικτό', en: 'Bilge pumps – if safely testable' } },
  { key: 'freshWater', label: { el: 'Νερό χρήσης – αντλία, βρύσες, ντους', en: 'Freshwater pump, taps, showers' } },
];

/** OK / DEF / N/T / N/A: each row's boxes, and what they mean. The form's legend explains the first three
 *  (inLegend); N/A is only a box on each row. */
export const VEF_RESULTS: readonly { value: CheckResult; mark: string; meaning: Bi; inLegend: boolean }[] = [
  { value: 'ok', mark: 'OK', meaning: { el: 'Ελέγχθηκε', en: 'Checked' }, inLegend: true },
  { value: 'def', mark: 'DEF', meaning: { el: 'Ζημιά ή βλάβη', en: 'Defect' }, inLegend: true },
  { value: 'nt', mark: 'N/T', meaning: { el: 'Δεν δοκιμάστηκε', en: 'Not tested' }, inLegend: true },
  { value: 'na', mark: 'N/A', meaning: { el: 'Δεν εφαρμόζεται', en: 'Not applicable' }, inLegend: false },
];

/** Photos' two boxes, as the paper has them: "☐ Yes ☐ No / ☐ Ναι ☐ Όχι". */
export const VEF_PHOTOS: readonly { value: PhotosAnswer; label: Bi }[] = [
  { value: 'yes', label: { el: 'Ναι', en: 'Yes' } },
  { value: 'no', label: { el: 'Όχι', en: 'No' } },
];
